"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  FormHelperText,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Text,
  Box,
  Tooltip,
  HStack,
  Checkbox,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface CustomFunctionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (functionConfig: any) => void;
  initialValues?: any;
}

export const CustomFunctionModal: React.FC<CustomFunctionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [parameters, setParameters] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [speakDuringExecution, setSpeakDuringExecution] = useState(true);
  const [speakAfterExecution, setSpeakAfterExecution] = useState(true);
  const [executionMessageDescription, setExecutionMessageDescription] =
    useState("");
  const [timeoutMs, setTimeoutMs] = useState(120000); // Default 2 minutes
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "");
      setDescription(initialValues.description || "");
      setUrl(initialValues.url || "");
      setParameters(
        initialValues.parameters
          ? JSON.stringify(initialValues.parameters, null, 2)
          : ""
      );
      setSpeakDuringExecution(
        initialValues.speak_during_execution !== undefined
          ? initialValues.speak_during_execution
          : true
      );
      setSpeakAfterExecution(
        initialValues.speak_after_execution !== undefined
          ? initialValues.speak_after_execution
          : true
      );
      setExecutionMessageDescription(
        initialValues.execution_message_description || ""
      );
      setTimeoutMs(initialValues.timeout_ms || 120000);
      setNameError(null);
      setJsonError(null);
    } else {
      // Reset to defaults when adding a new function
      setName("");
      setDescription("");
      setUrl("");
      setParameters(
        JSON.stringify(
          {
            type: "object",
            properties: {},
            required: [],
          },
          null,
          2
        )
      );
      setSpeakDuringExecution(true);
      setSpeakAfterExecution(true);
      setExecutionMessageDescription("");
      setTimeoutMs(120000);
      setNameError(null);
      setJsonError(null);
    }
  }, [initialValues]);

  const validateToolName = (name: string): boolean => {
    if (!name.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }

    if (name.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }

    // Check for spaces and invalid characters
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }

    setNameError(null);
    return true;
  };

  const validateJsonSchema = (schema: string): boolean => {
    if (!schema.trim()) {
      // Empty schema is valid (no parameters)
      setJsonError(null);
      return true;
    }

    try {
      const parsedSchema = JSON.parse(schema);

      // Basic validation for JSON Schema format
      if (parsedSchema.type !== "object") {
        setJsonError("Schema root type must be 'object'");
        return false;
      }

      setJsonError(null);
      return true;
    } catch (error) {
      setJsonError("Invalid JSON format");
      return false;
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    validateToolName(newName);
  };

  const handleParametersChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newParameters = e.target.value;
    setParameters(newParameters);
    // Don't validate on every keystroke to avoid annoying the user
  };

  const handleSave = () => {
    // Validate the tool name
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Validate the JSON schema
    if (!validateJsonSchema(parameters)) {
      toast({
        title: "Invalid JSON schema",
        description: jsonError,
        status: "error",
        duration: 3000,
      });
      return;
    }

    let parsedParameters = {};
    try {
      if (parameters) {
        parsedParameters = JSON.parse(parameters);
      }

      onSave({
        type: "custom",
        name,
        description,
        url,
        speak_during_execution: speakDuringExecution,
        speak_after_execution: speakAfterExecution,
        parameters: parsedParameters,
        execution_message_description: executionMessageDescription || undefined,
        timeout_ms: timeoutMs,
      });
      onClose();
    } catch (error) {
      console.error("Error parsing parameters:", error);
      setJsonError("Invalid JSON format. Please check your syntax.");
      toast({
        title: "Invalid JSON schema",
        description:
          "There was an error parsing your JSON schema. Please check the format.",
        status: "error",
        duration: 3000,
      });
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(parameters);
      setParameters(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (error) {
      console.error("Invalid JSON:", error);
      setJsonError("Invalid JSON format. Please check your syntax.");
      toast({
        title: "Invalid JSON",
        description: "Could not format JSON. Please check your syntax.",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Example JSON schemas
  const examples = {
    1: {
      type: "object",
      properties: {
        appointment_available_ts: {
          type: "string",
          description:
            "The timestamp of the appointment that is available for booking.",
        },
      },
      required: ["appointment_available_ts"],
    },
    2: {
      type: "object",
      properties: {
        city: {
          type: "string",
          description: "The city for which the weather is to be fetched.",
        },
      },
      required: ["city"],
    },
    3: {
      type: "object",
      properties: {
        appointment_available_ts: {
          type: "string",
          description:
            "The timestamp of the appointment that is available for booking.",
        },
        doctor_name: {
          type: "string",
          description: "An optional field to specify the name of the doctor.",
        },
      },
      required: ["appointment_available_ts"],
    },
  };

  const loadExample = (example: keyof typeof examples) => {
    setParameters(JSON.stringify(examples[example], null, 2));
    setJsonError(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent
        bg={isDark ? "gray.800" : "white"}
        textColor={isDark ? "white" : "gray.800"}
      >
        <ModalHeader style={{ display: "flex", alignItems: "center" }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4.33337 14.6667V10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M4.33337 3.33398V1.33398"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M11.6666 14.666V12.666"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M11.6666 6.00065V1.33398"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M6.33337 4.66732V8.66732C6.33337 9.40065 6.00004 10.0007 5.00004 10.0007H3.66671C2.66671 10.0007 2.33337 9.40065 2.33337 8.66732V4.66732C2.33337 3.93398 2.66671 3.33398 3.66671 3.33398H5.00004C6.00004 3.33398 6.33337 3.93398 6.33337 4.66732Z"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M13.6666 7.33333V11.3333C13.6666 12.0667 13.3333 12.6667 12.3333 12.6667H11C9.99996 12.6667 9.66663 12.0667 9.66663 11.3333V7.33333C9.66663 6.6 9.99996 6 11 6H12.3333C13.3333 6 13.6666 6.6 13.6666 7.33333Z"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
          <span style={{ marginLeft: "8px" }}>Custom Function</span>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                Name
              </FormLabel>
              <Input
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter the name of the custom function"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                Description
              </FormLabel>
              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter the description of the custom function"
                rows={3}
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                URL
              </FormLabel>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                placeholder="Enter the URL of the custom function"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
            </FormControl>

            <Divider />

            <FormControl isInvalid={!!jsonError}>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                Parameters (Optional)
                <Text fontSize={12} mt={1}>
                  JSON schema that defines the format in which the input to the
                  function is required.
                  {/* Please refer to the{" "}
                  <Link
                    href="https://docs.retellai.com/build/custom-function"
                    color={isDark ? "gray.300" : "gray.600"}
                    isExternal
                  >
                    docs <ExternalLinkIcon mx="-1px" />
                  </Link> */}
                </Text>
              </FormLabel>

              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                value={parameters}
                onChange={handleParametersChange}
                onBlur={() => validateJsonSchema(parameters)}
                placeholder="Enter JSON Schema here..."
                minHeight="200px"
                fontFamily="monospace"
                border="1px solid"
                _placeholder={{
                  color: isDark ? "gray.400" : "gray.600",
                }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{
                  borderColor: isDark ? "gray.500" : "gray.400",
                }}
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
                variant="outline"
              />
              {jsonError && <FormErrorMessage>{jsonError}</FormErrorMessage>}

              <HStack mt={3} spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                  background={isDark ? "gray.700" : "gray.200"}
                  textColor={isDark ? "whiteAlpha" : "gray.800"}
                  onClick={() => loadExample(1)}
                >
                  example 1
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                  background={isDark ? "gray.700" : "gray.200"}
                  textColor={isDark ? "whiteAlpha" : "gray.800"}
                  onClick={() => loadExample(2)}
                >
                  example 2
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                  background={isDark ? "gray.700" : "gray.200"}
                  textColor={isDark ? "whiteAlpha" : "gray.800"}
                  onClick={() => loadExample(3)}
                >
                  example 3
                </Button>
              </HStack>

              <Button
                mt={3}
                width="100%"
                variant="outline"
                onClick={formatJson}
                colorScheme="gray"
                _hover={{
                  bg: isDark ? "gray.600" : "gray.300",
                }}
                background={isDark ? "gray.700" : "gray.200"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
              >
                Format JSON
              </Button>
            </FormControl>

            <Divider />

            <Box>
              <Text
                fontWeight="medium"
                mb={2}
                color={isDark ? "gray.300" : "gray.700"}
              >
                Execution Settings
              </Text>

              <FormControl mb={3}>
                <Checkbox
                  isChecked={speakDuringExecution}
                  onChange={(e) => setSpeakDuringExecution(e.target.checked)}
                  colorScheme="gray"
                >
                  <Text color={isDark ? "gray.300" : "gray.700"}>
                    Speak during execution
                  </Text>
                </Checkbox>
                <FormHelperText color={isDark ? "gray.400" : "gray.600"} ml={6}>
                  Agent will say something like "One moment, let me check that."
                  when executing
                </FormHelperText>
              </FormControl>

              {speakDuringExecution && (
                <FormControl mb={3}>
                  <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                    Execution message description
                  </FormLabel>
                  <Input
                    value={executionMessageDescription}
                    onChange={(e) =>
                      setExecutionMessageDescription(e.target.value)
                    }
                    placeholder="The message to say when calling this tool"
                    _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                    textColor={isDark ? "white" : "gray.800"}
                    bg={isDark ? "gray.700" : "white"}
                    color={isDark ? "white" : "gray.800"}
                    borderColor={isDark ? "gray.600" : "gray.300"}
                    _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                    variant="outline"
                    _focus={{
                      borderColor: isDark ? "gray.100" : "gray.800",
                    }}
                  />
                </FormControl>
              )}

              <FormControl mb={3}>
                <Checkbox
                  isChecked={speakAfterExecution}
                  onChange={(e) => setSpeakAfterExecution(e.target.checked)}
                  colorScheme="gray"
                >
                  <Text color={isDark ? "gray.300" : "gray.700"}>
                    Speak after execution
                  </Text>
                </Checkbox>
                <FormHelperText color={isDark ? "gray.400" : "gray.600"} ml={6}>
                  Agent will provide an update when the function call completes
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                  Timeout (ms)
                  <Tooltip label="1,000 ms (1s) minimum, 600,000 ms (10 min) maximum">
                    <InfoIcon ml={1} boxSize={3} />
                  </Tooltip>
                </FormLabel>
                <NumberInput
                  value={timeoutMs}
                  onChange={(valueString) => setTimeoutMs(Number(valueString))}
                  min={1000}
                  max={600000}
                  step={1000}
                  textColor={isDark ? "white" : "gray.800"}
                  bg={isDark ? "gray.700" : "white"}
                  color={isDark ? "white" : "gray.800"}
                  borderColor={isDark ? "gray.600" : "gray.300"}
                  _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                  variant="outline"
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText color={isDark ? "gray.400" : "gray.600"}>
                  Maximum time the tool can run before timeout (default: 120,000
                  ms / 2 min)
                </FormHelperText>
              </FormControl>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSave}
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
